<template>
  <div class="bg-neutral-50 py-8 min-h-screen">
    <div class="max-w-[820px] mx-auto px-4 md:px-8">
      <div class="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-neutral-900">投稿案例</h1>
          <p class="text-sm text-neutral-500 mt-1">
            分享你的 AI 应用实践，审核通过后会在案例广场展示
          </p>
        </div>
        <router-link to="/cases" class="text-sm text-blue-600 hover:underline"
          >← 返回案例广场</router-link
        >
      </div>

      <div class="bg-white rounded-xl shadow-sm p-6">
        <!-- 主题 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2"
            >主题 <span class="text-red-500">*</span></label
          >
          <input
            v-model="form.title"
            maxlength="100"
            placeholder="例如：用 AI 十分钟生成单元作业设计"
            class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>

        <!-- 应用场景 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2"
            >应用场景 <span class="text-red-500">*</span></label
          >
          <select
            v-model="form.scenario"
            class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">请选择</option>
            <option v-for="s in scenarios" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <!-- 关键词 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2">案例关键词</label>
          <input
            v-model="form.keywords"
            placeholder="多个关键词用逗号分隔，如：作业设计,单元整体,效率提升"
            class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>

        <!-- 获奖等级：级别 + 等次 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2"
            >获奖等级 <span class="text-xs text-neutral-400">（选填）</span></label
          >
          <div class="grid grid-cols-2 gap-3">
            <select
              v-model="form.awardLevel"
              class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">获奖级别</option>
              <option v-for="a in awardLevels" :key="a" :value="a">{{ a }}</option>
            </select>
            <select
              v-model="form.awardRank"
              class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">几等奖</option>
              <option v-for="a in awardRanks" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
        </div>

        <!-- 核心图示 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2"
            >核心图示 <span class="text-xs text-neutral-400">（最多 3 张）</span></label
          >
          <div class="flex flex-wrap gap-3">
            <div v-for="(img, i) in form.images" :key="i" class="relative w-28 h-28">
              <img :src="img" class="w-full h-full object-cover rounded-lg" alt="" />
              <button
                class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs"
                @click="removeImage(i)"
              >
                ×
              </button>
            </div>
            <label
              v-if="form.images.length < 3"
              class="w-28 h-28 border-2 border-dashed border-neutral-300 rounded-lg flex flex-col items-center justify-center text-neutral-400 cursor-pointer hover:border-blue-500 hover:text-blue-500"
            >
              <i class="fa fa-plus text-xl"></i>
              <span class="text-xs mt-1">{{ uploading ? "上传中…" : "添加图片" }}</span>
              <input
                type="file"
                accept="image/*"
                class="hidden"
                :disabled="uploading"
                @change="pickImage"
              />
            </label>
          </div>
        </div>

        <!-- 主要内容 -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-2"
            >主要内容简述 <span class="text-red-500">*</span></label
          >
          <textarea
            v-model="form.content"
            maxlength="800"
            rows="8"
            placeholder="这个案例解决了什么问题、怎么做的、效果如何…（不超过 800 字）"
            class="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
          ></textarea>
          <div class="text-right text-xs text-neutral-400 mt-1">
            {{ form.content.length }} / 800
          </div>
        </div>

        <div class="flex items-center gap-3 flex-wrap">
          <button
            class="px-6 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
            :disabled="submitting"
            @click="submit"
          >
            {{ submitting ? "提交中…" : "提交审核" }}
          </button>
          <span class="text-xs text-neutral-400"
            >提交后由管理员审核，通过后才会公开；图示查看时会自动带上你的署名水印</span
          >
        </div>
      </div>

      <!-- 我的投稿 -->
      <div class="bg-white rounded-xl shadow-sm p-6 mt-6">
        <h2 class="font-bold text-neutral-900 mb-4">我的投稿</h2>
        <div v-if="mineLoading" class="text-center py-8 text-neutral-400 text-sm">
          加载中…
        </div>
        <div v-else-if="mine.length === 0" class="text-center py-8 text-neutral-400 text-sm">
          还没有投稿记录
        </div>
        <div v-else class="divide-y divide-neutral-100">
          <div
            v-for="m in mine"
            :key="m.id"
            class="py-3 flex items-center gap-3 flex-wrap"
          >
            <span class="flex-1 min-w-[200px] text-sm text-neutral-800">{{
              m.title
            }}</span>
            <span class="text-xs text-neutral-400">{{ fmtTime(m.createTime) }}</span>
            <span class="text-xs px-2 py-0.5 rounded" :class="statusClass(m.status)">{{
              statusText(m.status)
            }}</span>
            <span
              v-if="m.status === 2 && m.examInfo"
              class="text-xs text-red-500 w-full"
              >驳回理由：{{ m.examInfo }}</span
            >
            <button
              class="text-xs text-red-500 hover:underline"
              @click="removeMine(m)"
            >
              删除
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "SubmitCasePage",
  data() {
    return {
      form: {
        id: "",
        title: "",
        scenario: "",
        keywords: "",
        awardLevel: "",
        awardRank: "",
        content: "",
        images: [],
      },
      scenarios: [
        "课堂教学",
        "备课教研",
        "作业测评",
        "班级管理",
        "家校沟通",
        "校园活动",
        "行政办公",
        "教师成长",
        "其它",
      ],
      awardLevels: ["国家级", "省级", "市级", "区县级", "校级", "其他"],
      awardRanks: [
        "特等奖",
        "一等奖",
        "二等奖",
        "三等奖",
        "优秀奖",
        "入围奖",
        "其他",
      ],
      submitting: false,
      uploading: false,
      mine: [],
      mineLoading: false,
    };
  },
  created() {
    if (!requireLogin(this, "投稿案例")) {
      this.$router.push("/cases");
      return;
    }
    this.getMine();
    const editId = this.$route.query.id;
    if (editId) this.loadMineDetail(editId);
  },
  methods: {
    // 编辑：回填自己的案例（含原图）
    async loadMineDetail(id) {
      try {
        const res = await this.$axios({ url: "case/mine/" + id });
        const d = res.code === 200 ? res.data : null;
        if (!d) {
          ElMessage.error("案例不存在或无权编辑");
          return;
        }
        let imgs = [];
        try {
          imgs = JSON.parse(d.images || "[]");
        } catch (e) {
          imgs = [];
        }
        this.form = {
          id: d.id,
          title: d.title || "",
          scenario: d.scenario || "",
          keywords: d.keywords || "",
          awardLevel: d.awardLevel || "",
          awardRank: d.awardRank || "",
          content: d.content || "",
          images: Array.isArray(imgs) ? imgs : [],
        };
      } catch (e) {
        ElMessage.error("加载案例失败，请稍后重试");
      }
    },
    async pickImage(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (this.form.images.length >= 3) {
        ElMessage.warning("核心图示最多 3 张");
        return;
      }
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("UploadType", "MutipleImage");
        const d = new Date();
        const ymd =
          d.getFullYear() +
          "/" +
          String(d.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(d.getDate()).padStart(2, "0");
        fd.append("businessType", "case/" + ymd);
        const res = await this.$axios({
          url: "upload",
          method: "POST",
          data: fd,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.code === 200) {
          this.form.images.push(res.data.uploads.files[0].o.fullPath);
        } else {
          ElMessage.error(res.msg || "上传失败");
        }
      } catch (err) {
        ElMessage.error("上传失败：" + (err.message || ""));
      } finally {
        this.uploading = false;
      }
    },
    removeImage(i) {
      this.form.images.splice(i, 1);
    },
    async submit() {
      if (!this.form.title.trim()) return ElMessage.warning("请填写主题");
      if (!this.form.scenario) return ElMessage.warning("请选择应用场景");
      if (!this.form.content.trim()) return ElMessage.warning("请填写主要内容简述");
      this.submitting = true;
      try {
        const payload = Object.assign({}, this.form, {
          images: JSON.stringify(this.form.images),
        });
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({ url: "case", method: "PUT", data: { data } });
        if (res.code === 200) {
          ElMessage.success(res.data || "投稿成功，等待审核");
          if (this.form.id) {
            this.$router.push("/user");
            return;
          }
          this.form = {
            id: "",
            title: "",
            scenario: "",
            keywords: "",
            awardLevel: "",
            awardRank: "",
            content: "",
            images: [],
          };
          this.getMine();
        } else {
          ElMessage.error(res.msg || "提交失败");
        }
      } catch (err) {
        ElMessage.error((err && err.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    },
    async getMine() {
      this.mineLoading = true;
      try {
        const data = await this.$aesEncrypt({ page: 1, pageSize: 50, status: -1 });
        const res = await this.$axios({
          url: "case/mine",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) this.mine = res.data.list || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.mineLoading = false;
      }
    },
    removeMine(m) {
      ElMessageBox.confirm(`确定删除投稿「${m.title}」吗？`, "删除确认", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          const res = await this.$axios({
            url: "case/" + m.id,
            method: "DELETE",
          });
          if (res.code === 200) {
            ElMessage.success("已删除");
            this.getMine();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        })
        .catch(() => {});
    },
    statusText(s) {
      return ["待审核", "已通过", "已驳回"][s] || "";
    },
    statusClass(s) {
      return [
        "bg-amber-50 text-amber-600",
        "bg-green-50 text-green-600",
        "bg-red-50 text-red-600",
      ][s] || "";
    },
    fmtTime(t) {
      return t ? String(t).replace("T", " ").substring(0, 16) : "";
    },
  },
};
</script>
